/**
 * Module-level singleton flag that prevents click events from firing
 * immediately after a drag operation.
 *
 * Browser event order is: dragstart → (dragover)* → drop → dragend → click
 * Setting the flag on dragstart and clearing it asynchronously in dragend
 * means the flag is still true when click fires (same task), so click
 * handlers can bail out early.
 */
const POST_DRAG_CLICK_DELAY_MS = 100;
const STALE_DRAG_TIMEOUT_MS = 1500;

let dragInProgress = false;
let suppressClicksUntil = 0;
let staleDragTimer: ReturnType<typeof setTimeout> | null = null;

export function notifyDragStart(): void {
  dragInProgress = true;
  suppressClicksUntil = Date.now() + STALE_DRAG_TIMEOUT_MS;

  if (staleDragTimer) clearTimeout(staleDragTimer);
  staleDragTimer = setTimeout(() => {
    dragInProgress = false;
    suppressClicksUntil = 0;
    staleDragTimer = null;
  }, STALE_DRAG_TIMEOUT_MS);
}

export function notifyDragEnd(): void {
  dragInProgress = false;
  suppressClicksUntil = Date.now() + POST_DRAG_CLICK_DELAY_MS;

  if (staleDragTimer) clearTimeout(staleDragTimer);
  staleDragTimer = setTimeout(() => {
    suppressClicksUntil = 0;
    staleDragTimer = null;
  }, POST_DRAG_CLICK_DELAY_MS);
}

/**
 * Swallows the click that trails a pointerup which removed the element under the pointer
 * (e.g. a date picker that closes on pointerup); otherwise that click lands on whatever
 * is revealed underneath. Gives up after timeoutMs if no click arrives.
 */
export function swallowNextClick(timeoutMs = 500): void {
  const swallow = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    cleanup();
  };
  const cleanup = () => {
    window.removeEventListener('click', swallow, true);
    clearTimeout(timer);
  };
  window.addEventListener('click', swallow, true);
  const timer = setTimeout(cleanup, timeoutMs);
}

export function shouldSuppressClick(): boolean {
  if (!dragInProgress && Date.now() >= suppressClicksUntil) {
    suppressClicksUntil = 0;
    return false;
  }

  return dragInProgress || suppressClicksUntil > Date.now();
}
